import { Grid, Skeleton, useMantineColorScheme } from "@mantine/core";
import { AccordionItem } from "../AccordianItem";
import "./skeleton.scss";
import { useMemo } from "react";

// Reusable skeleton field column
const SkeletonFieldCol = () => (
  <Grid.Col span={3} style={{ marginRight: "6rem" }}>
    <Skeleton height={10} width={"30%"} mb={12} />
    <Skeleton height={35} />
  </Grid.Col>
);

// Reusable skeleton row with 3 columns
const SkeletonFieldRow = () => (
  <Grid gutter="md" className="mt-2 mb-2">
    <SkeletonFieldCol />
    <SkeletonFieldCol />
    <SkeletonFieldCol />
  </Grid>
);

// Reusable result content skeleton
const ResultContentSkeleton = () => (
  <>
    <Grid.Col span={10}>
      <Skeleton height={30} width={"20%"} />
      <br />
      <Skeleton height={20} width={"30%"} />
      <br />
      <Skeleton height={20} width={"50%"} />
      <br />
      <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
        {Array.from({ length: 4 }).map((_, idx) => (
          <Skeleton key={idx} height={80} width="10%" mb={16} radius="md" />
        ))}
      </div>
      <br />
      <div
        style={{
          display: "flex",
          gap: "3rem",
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        {Array.from({ length: 5 }).map((_, idx) => (
          <Skeleton key={idx} height={180} width="30%" mb={16} radius="md" />
        ))}
      </div>
    </Grid.Col>
    <Grid.Col
      span={2}
      style={{ display: "flex", flexDirection: "column", gap: "12px" }}
    >
      {Array.from({ length: 5 }).map((_, idx) => (
        <Skeleton key={idx} height={30} width={"80%"} />
      ))}
    </Grid.Col>
  </>
);

export const ProgressSkeleton = () => {
  const { colorScheme } = useMantineColorScheme();

  const progressItems = useMemo(
    () =>
      Array.from({ length: 5 }).map((_, idx) => (
        <div key={idx} style={{ display: "contents" }}>
          <Skeleton height={50} circle mb="xl" ml={8} mt={10} />
          {idx < 4 && <Skeleton height={8} radius="xl" width="12%" />}
        </div>
      )),
    []
  );

  return (
    <div
      style={{
        display: "flex",
        backgroundColor: colorScheme === "dark" ? "#505559" : "white",
        alignItems: "center",
        boxShadow: "0px 4px 4px rgba(90, 116, 148, 0.3)",
        position: "relative",
      }}
      data-testid="progress-skeleton"
    >
      {progressItems}
    </div>
  );
};

export const SideBarSkeleton = () => {
  return (
    <div
      style={{ display: "flex", flexDirection: "column", marginTop: "1rem" }}
    >
      <Skeleton height={20} width={"50%"} mb={12} />
      <Skeleton height={20} mb={12} />

      <Skeleton height={20} width={"50%"} mb={12} />
    </div>
  );
};

export const AccordianSkeleton = () => {
  const header = () => <Skeleton height={30} radius="xl" width="50%" />;

  const child = () => (
    <>
      <SkeletonFieldRow />
      <SkeletonFieldRow />
      <SkeletonFieldRow />
    </>
  );

  return (
    <AccordionItem
      id={1}
      header={header()}
      key={1}
      children={child()}
      isSkeleton={true}
    />
  );
};

export const ResultTabSkeleton = () => (
  <>
    <Grid
      gutter="md"
      className="mb-2"
      style={{ marginTop: "2%" }}
      justify="center"
    >
      <Grid.Col span={4}>
        <Skeleton height={40} />
      </Grid.Col>
      <Grid.Col span={4} style={{ display: "flex", columnGap: "5px" }}>
        {Array.from({ length: 4 }).map((_, idx) => (
          <Skeleton key={idx} height={40} />
        ))}
      </Grid.Col>
    </Grid>
    <Grid
      gutter="md"
      className="mb-2"
      style={{ marginTop: "2%", marginLeft: "4%" }}
    >
      <ResultContentSkeleton />
    </Grid>
  </>
);

export const ChartSkeleton = () => {
  const { colorScheme } = useMantineColorScheme();

  const yTicks = useMemo(
    () =>
      Array.from({ length: 5 }, (_, index) => (
        <div key={index} className="y-tick"></div>
      )),
    []
  );

  const bars = useMemo(
    () =>
      Array.from({ length: 40 }, (_, index) => (
        <div key={index} className="bared"></div>
      )),
    []
  );

  const xTicks = useMemo(
    () =>
      Array.from({ length: 40 }, (_, index) => (
        <div key={index} className="x-tick"></div>
      )),
    []
  );

  return (
    <div
      className={
        colorScheme === "dark" ? "chart-wrapper-dark" : "chart-wrapper-light"
      }
      aria-label="Loading chart data"
    >
      <div className="y-axis-wrapper" aria-hidden="true">
        <div className="y-axis-line"></div>
        {yTicks}
      </div>
      <div className="chart-loader">{bars}</div>
      <div className="x-axis" aria-hidden="true">
        {xTicks}
      </div>
      <div className="x-axis-line"></div>
    </div>
  );
};

export const ResultTabNewSkeleton = () => (
  <>
    <Grid
      gutter="md"
      className="mb-2"
      style={{ marginTop: "2%", marginLeft: "4%" }}
    >
      <ResultContentSkeleton />
    </Grid>
  </>
);

export const CompareTableSkeleton = () => {
  const headerCols = ["Metric", "Scenario A", "Scenario B", "Scenario C", "Delta", "Notes"];
  return (
    <Grid gutter="md" className="mb-2" style={{ marginTop: "1%", marginLeft: "1%", marginRight: "1%" }}>
      <Grid.Col span={12}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Skeleton height={32} width={120} />
            <Skeleton height={32} width={120} />
          </div>
          <Skeleton height={32} width={36} />
        </div>

        {/* table header */}
        <div style={{ display: 'flex', gap: 12, padding: '8px 12px', background: 'transparent', alignItems: 'center' }}>
          {headerCols.map((h, i) => (
            <div key={i} style={{ flex: i === 0 ? '0 0 30%' : '1 1 0', minWidth: i === 0 ? 200 : 100 }}>
              <Skeleton height={18} width={i === 0 ? 140 : 100} />
            </div>
          ))}
        </div>

        {/* table rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
          {Array.from({ length: 10 }).map((_, rowIdx) => (
            <div key={rowIdx} style={{ display: 'flex', gap: 12, padding: '10px 12px', alignItems: 'center', borderRadius: 4 }}>
              <div style={{ flex: '0 0 30%', minWidth: 200 }}>
                <Skeleton height={16} width={160} />
              </div>
              <div style={{ display: 'flex', gap: 10, flex: 1, alignItems: 'center' }}>
                <Skeleton height={16} width={100} />
                <Skeleton height={16} width={100} />
                <Skeleton height={16} width={100} />
                <Skeleton height={16} width={80} />
                <Skeleton height={16} width={120} />
              </div>
            </div>
          ))}
        </div>
      </Grid.Col>
    </Grid>
  );
};

// Skeleton for selected KPI cards while KPI data is loading
export const SelectedKpiSkeleton = ({ count = 4 }) => {
  const { colorScheme } = useMantineColorScheme();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
      data-testid="selected-kpi-skeleton"
    >
      {/* <Skeleton height={24} width={"40%"} mb={12} /> */}
      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        {Array.from({ length: count }).map((_, idx) => (
          <div
            key={idx}
            style={{
              flex: '0 0 22%',
              minWidth: 180,
              padding: 8,
              border: 'none',
              boxShadow: 'none',
              background: 'transparent',
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Skeleton circle height={48} width={48} />
              <div style={{ flex: 1 }}>
                <Skeleton height={12} width={"60%"} mb={6} />
                <Skeleton height={14} width={"40%"} />
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <Skeleton height={8} width={"100%"} radius="xl" />
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <Skeleton height={36} width={"48%"} />
                <Skeleton height={36} width={"48%"} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
